import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
  withViewTransitions
} from '@angular/router';
import { provideUnleashProxy } from '@karelics/angular-unleash-proxy-client';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import Aura from '@primeng/themes/aura';
import { CacheInterceptor, SpinnerLoaderInterceptor, TokenInterceptor } from '@shared/http-interceptors';
import { CustomHeaderInterceptor } from '@shared/http-interceptors/custom-header-interceptor';
import { ErrorInterceptor } from '@shared/http-interceptors/error-interceptor';
import { AppInitService } from '@shared/services/app-init.service';
import { AuthGuardService } from '@shared/services/auth-guard.service';
import { AuthService } from '@shared/services/auth.service';
import { ChangePasswordService } from '@shared/services/change-password.service';
import { ErrorConnectorService } from '@shared/services/error-connector.service';
import { HotjarService } from '@shared/services/hotjar.service';
import { ProgressSpinnerService } from '@shared/services/progress-spinner.service';
import { UnleashProxyService } from '@shared/services/unleash-proxy.service';
import { AuthEffects } from '@shared/store/effects/auth.effects';
import { ChangePasswordEffects } from '@shared/store/effects/change-password.effects';
import { ForgotPasswordEffects } from '@shared/store/effects/forgot-password.effects';
import { LayoutEffects } from '@shared/store/effects/layout.effects';
import { ManagedStoreEffects } from '@shared/store/effects/managed-store.effects';
import { MenuEffects } from '@shared/store/effects/menu.effects';
import { appReducers } from '@shared/store/reducers/app.reducers';
import { clearState } from '@shared/store/reducers/clear-state';
import { CookiesUtil } from '@shared/utils/cookies-util';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { CookieService } from 'ngx-cookie-service';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';

import { APP_ROUTES } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { enableMockWorker } from './mocks/browser';
export function InitializeAppFactory(appInitService: AppInitService) {
  if (environment.MSW) {
    console.log('Enable Mock Service Worker(MSW)');
    enableMockWorker();
  }
  return appInitService.load();
}

const loggerModuleOption = () => {
  if (environment.production) {
    return NgxLoggerLevel.OFF;
  } else {
    return NgxLoggerLevel.TRACE;
  }
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const scrollConfig: InMemoryScrollingOptions = {
  anchorScrolling: 'enabled'
};

const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling(scrollConfig);

bootstrapApplication(AppComponent, {
  providers: [
    providePrimeNG({
      ripple: true,
      theme: {
        preset: Aura,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'primeng'
          },
          darkModeSelector: false
        }
      }
    }),
    importProvidersFrom(
      BrowserAnimationsModule,
      StoreModule.forRoot(appReducers, { metaReducers: [clearState] }),
      EffectsModule.forRoot([
        AuthEffects,
        ForgotPasswordEffects,
        ChangePasswordEffects,
        LayoutEffects,
        MenuEffects,
        ManagedStoreEffects
      ]),
      LoggerModule.forRoot({
        level: loggerModuleOption(),
        serverLogLevel: NgxLoggerLevel.OFF
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideStoreDevtools({
      // TODO: review config later
      maxAge: 25, // Retains last 25 states
      // logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true // If set to true, the connection is established within the Angular zone
    }),
    UnleashProxyService,
    TranslateService,
    CookieService,
    CookiesUtil,
    AuthService,
    AppInitService,
    AuthGuardService,
    ChangePasswordService,
    ErrorConnectorService,
    DialogService,
    ConfirmationService,
    ProgressSpinnerService,
    HotjarService,
    provideAppInitializer(() => {
      // ตั้ง context ของ flag ที่ผูกกับร้านก่อนอ่าน toggle ครั้งแรก การสลับร้านจาก topbar จะ reload หน้า
      // อยู่แล้ว เส้นนั้นจึงถูกครอบด้วยจุดนี้ด้วย
      inject(UnleashProxyService).updateStoreContext();

      const initializerFn = InitializeAppFactory(inject(AppInitService));
      return initializerFn();
    }),
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerLoaderInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(APP_ROUTES, inMemoryScrollingFeature, withViewTransitions()),
    provideCharts(withDefaultRegisterables()),
    UnleashProxyService,
    provideUnleashProxy({
      url: environment.unleashUrl,
      clientKey: environment.unleashClientKey,
      appName: environment.unleashAppName,
      environment: environment.unleashEnvironment,
      customHeaders: environment.unleashCustomHeaders,
      refreshInterval: environment.unleashRefreshInterval
    })
  ]
}).catch((err) => console.error(err));
